import React, {
	Dispatch,
	Fragment,
	ReactNode,
	ReactText,
	SetStateAction,
	useEffect,
	useState
} from "react";
import ProductsActionCreators from "@/Actions/Products";
import SettingsActionCreators from "@/Actions/Settings";
import UsersActionCreators from "@/Actions/User";
import RenderCustomFieldFormItem from "@molecules/ZRenderCustomFields";
import RoleBasedDisplayWrapper from "@/Components/UI/RoleBasedDisplayWrapper";
import { isEtilizeVendorProductId } from "@/Components/Utils";
import { useFeatureFlag } from "@/Hooks/useFeature";
import {
	handleMarginChange,
	handleMarkupChange,
	handleMarkupMargin,
	handleSellPriceCalculation
} from "@/Components/Utils/PricingCalculator";
import { ZeroOrNegative as AllowZeroOrNegative } from "@/Hooks/Integrations/interface";
import { useAllowZeroOrNegativeQuantity } from "@/Hooks/Integrations/useAllowZeroOrNegativeQuantity";
import { useAddOrEditSupplier } from "@/Hooks/Supplier/useAddOrEditSupplier";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { GlobalState } from "@/Reducers/interface";
import {
	getProductDetails,
	getQuoteBlockProductPricing
} from "@/Services/Products";
import { Form, Icon } from "@ant-design/compatible";
import { FormComponentProps } from "@ant-design/compatible/lib/form";
import ZFeatureAccess from "@atoms/ZFeatureAccess";
import round from "@molecules/ConfigSellPriceRoundingOption/RoundingUtils";
import TaxSelect from "@molecules/TaxSelect";
import ZSupplierSelect from "@organisms/ZSupplierSelect";
import ProductUOMWarning from "@pages/Documents/DocumentDetailsPage/molecules/ProductUOMWarning";
import AddProductDrawerV2 from "@pages/Drawers/Products/Product";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";
import AddTaxCategoryDrawer from "@pages/TaxListPage/Drawers/TaxCategory";
import ZAddSupplier from "@templates/ZSuppliers/ZDrawers/ZAddOrEditSupplier";
import { SupplierFormValues } from "@templates/ZSuppliers/ZDrawers/ZAddOrEditSupplier/interface";
import { UploadResult } from "@uppy/core";
import { ZInputNumber } from "@atoms";
import { EColors } from "@atoms/ZColors/interface";
import ZIcon, { Icons } from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import ZImage from "@atoms/ZImage";
import ZModal from "@atoms/ZModal";
import { IModalProps } from "@atoms/ZModal/interface";
import ZSelect from "@atoms/ZSelect";
import ZTooltip from "@atoms/ZTooltip";
import ZTypography from "@atoms/ZTypography";
import ZCheckbox from "@atoms/ZCheckbox";
import ZCheckboxGroup from "@atoms/ZCheckboxGroup";
import {
	capitalizeFirstLetter,
	DefaultErrorMessage,
	fallbackImageURL,
	UppyFileMetaData
} from "@/Contracts";
import { Document, DocumentStatus } from "@/Contracts/Documents";
import {
	Distributor,
	distributorLabel,
	ProductBillingType,
	productBillingTypeOptions,
	ProductPricingPlan,
	ProductSource,
	ProductType,
	ratailPriceLabel,
	RetailPriceInfo
} from "@/Contracts/Products";
import {
	AddQuoteProductCatalogDrawerFormValues,
	BundledQuoteProduct,
	EditQuoteProductCatalogDrawerFormValues,
	QuoteProduct
} from "@/Contracts/ProposalBuilder/Block";
import {
	CustomField,
	CustomFieldMandatoryFieldDetailsValues,
	DynamicPricingDistributors,
	RoundingDetails,
	Supplier,
	SyncOption
} from "@/Contracts/Settings";
import {
	AllRolePermissions,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";
import ZFileUpload from "@molecules/ZFileUpload";
import { UppyAllowedFileTypes as AllowedFileTypes } from "@molecules/ZFileUpload/interface";
import ZMessage from "@molecules/ZMessage";
import ZSpin from "@molecules/ZSpin";
import {
	currencyFormatter,
	getCurrencySettings,
	percentageFormatter
} from "@/Utils/Currency";
import { Col, Divider, Row, Select, Switch } from "antd";
import { RadioChangeEvent } from "antd/es/radio";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Input from "antd/lib/input";
import InputNumber from "antd/lib/input-number";
import TextArea from "antd/lib/input/TextArea";
import he from "he";
import { useSelector } from "react-redux";
import { AddProductToQuoteBlockDrawer } from "../AddProductToQuoteBlockDrawer";
import BundleProductDrawerTable from "../DocumentProductDrawersComponents/BundleProductDrawerTable";
import DynamicPricingOptions from "../DocumentProductDrawersComponents/DynamicPricingOptions/DynamicPricingOptions";
import { ProductPricingOptions } from "../DocumentProductDrawersComponents/ProductPricingOptions/ProductPricingOptions";
import "./../ConfigureDocumentProductDrawer.less";
import {
	getProductImageUrl,
	processCustomFieldFormValues
} from "@/Utils/common";
import ZDrawer from "@molecules/ZDrawer";
import { useBundlesSettings } from "@/Hooks";
import ZButton from "@atoms/ZButton";
import ZInput from "@molecules/ZInput";
import useDispatch from "@/Components/Utils/useDispatch";
import { InputNumberWidths } from "@atoms/ZInputNumber";

const { ZText } = ZTypography;

interface bundleSettingDetail {
	readonly pricingDisabled: boolean;
	readonly showChildItems: boolean;
	readonly showQuantity: boolean;
	readonly showPrice: boolean;
	readonly setShowChildItems: Dispatch<SetStateAction<boolean>>;
	readonly setShowQuantity: Dispatch<SetStateAction<boolean>>;
	readonly setShowPrice: Dispatch<SetStateAction<boolean>>;
	readonly handleChangeChildItems: () => void;
	readonly handleChangeQuantity: () => void;
	readonly handleChangePrice: () => void;
}

export interface Props extends FormComponentProps<QuoteProduct> {
	onClose?: (e: any) => void;
	readonly okText: string;
	readonly defaultData:
		| AddQuoteProductCatalogDrawerFormValues
		| EditQuoteProductCatalogDrawerFormValues
		| null;
	readonly onSubmit?: (quoteProduct: QuoteProduct) => void;
	readonly currentActiveDocument: Document | null;
	readonly isAddingProduct?: boolean;
	readonly isBundleProduct?: boolean;
	readonly hasEditProductCostPricePermission?: boolean;
	readonly isNotABundleProduct?: boolean;
	readonly isSupplierManagementEnabled?: boolean;
	readonly isSupplierSubmitting?: boolean;
	readonly getSuppliers?: () => void;
	readonly getSupplierById?: (supplierId?: string) => void;
	readonly addSupplier?: (
		supplierValues: SupplierFormValues
	) => Promise<Supplier | undefined>;
	readonly isReadOnly?: boolean;
}

export interface IHandlePricingIdChangeProps {
	value: string | null;
	radioChangeEvent: RadioChangeEvent | null;
}

const DocumentProductConfigureDrawerForm: React.FC<Props> = props => {
	const movePreferencesToGlobal = useFeatureFlag("move_preferences_global");
	const isBundleEnhancementEnabled = useFeatureFlag(
		"enable_bundles_enchancements"
	);

	const {
		pricingDisabled,
		showChildItems,
		showQuantity,
		showPrice,
		setShowChildItems,
		setShowQuantity,
		setShowPrice,
		handleChangeChildItems,
		handleChangeQuantity,
		handleChangePrice
	} = useBundlesSettings();

	const bundleSettingDetail: bundleSettingDetail = {
		pricingDisabled,
		showChildItems,
		showQuantity,
		showPrice,
		setShowChildItems,
		setShowQuantity,
		setShowPrice,
		handleChangeChildItems,
		handleChangeQuantity,
		handleChangePrice
	};

	const hasTaxEditPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[AllRolePermissions.TAX_SETUP_UPDATE]
	);

	const isNotABundleProduct = !props.isBundleProduct;
	const allowQuantityZeroOrNegative: AllowZeroOrNegative =
		useAllowZeroOrNegativeQuantity();

	const { addSupplier, getSupplierById } = useAddOrEditSupplier();
	const {
		isLoading,
		taxCategoriesList,
		defaultPreferences,
		addTaxCategoryDrawerOpen,
		unitOfMeasureMapping,
		userCompany,
		suppliers,
		addOrEditSupplierDrawerOpen,
		customIntegrationList,
		customFieldListProduct
	} = useSelector((state: GlobalState) => {
		return {
			isLoading: state.products.isLoading,
			taxCategoriesList: state.user.taxCategoriesList,
			defaultPreferences: movePreferencesToGlobal
				? state.user.userCompany.settings?.preference_settings
						?.product_preference_settings
				: state.user.userData?.user_settings?.preference_settings
						?.product_preference_settings,
			addTaxCategoryDrawerOpen: state.user.addTaxCategoryDrawerOpen,
			unitOfMeasureMapping:
				state.user.userCompany?.settings
					.default_unit_of_measure_mapping,
			userCompany: state.user.userCompany,
			suppliers: state.settings.suppliers,
			addOrEditSupplierDrawerOpen:
				state.settings.addOrEditSupplierDrawerOpen,
			customIntegrationList:
				state.user.userCompany.integrations.custom_distributor,
			customFieldListProduct:
				state.customFields.customFieldListWithoutFormulaFields.item
		};
	});

	const [
		catalogProductImageUploadModalOpen,
		setCatalogProductImageUploadModalOpen
	] = useState(false);
	const [bundleAddProductDrawerOpen, setBundleAddProductDrawerOpen] =
		useState(false);
	const [createProductButtonVisible, setCreateProductButtonVisible] =
		useState(false);
	const [catalogProductImages, setCatalogProductImages] = useState(
		new Array<string>()
	);
	const [selectedImages, setSelectedImages] = useState(new Array<string>());
	const [pricing, setPricing] = useState<ProductPricingPlan[] | null>(null);
	const [selectedDistributor, setSelectedDistributor] = useState<
		string | undefined
	>(props.defaultData?.selected_distributor);
	const [selectedWarehouse, setSelectedWarehouse] = useState<
		string | undefined
	>(undefined);
	const [activeBundledQuoteProducts, setActiveBundledQuoteProducts] =
		useState<BundledQuoteProduct[]>(
			props.defaultData?.bundled_products || []
		);
	const [
		editBundledQuoteProductDefaultState,
		setEditBundledQuoteProductDefaultState
	] = useState<BundledQuoteProduct | null>(null);
	const [customFieldList, setCustomFieldList] = useState<CustomField[]>(
		new Array<CustomField>()
	);
	const [vendorCustomFields, setVendorCustomFields] = useState<CustomField[]>(
		new Array<CustomField>()
	);
	const [selectedPricingId, setSelectedPricingId] = useState<
		string | null | undefined
	>(props.defaultData?.selected_pricing_id);
	const [isAutoUpdatePricingEnabled, setIsAutoUpdatePricingEnabled] =
		useState(false);
	const [isAutoUpdateSwitchEnabled, setIsAutoUpdateSwitchEnabled] =
		useState(true);
	const [, setCanEditCostPrice] = useState(true);
	const [selectedDistributors, setSelectedDistributors] = useState<
		DynamicPricingDistributors[] | null | undefined
	>(props.defaultData?.auto_price_update_details?.selected_distributors);
	const [roundingDetails, setRoundingDetails] = useState<
		RoundingDetails | undefined
	>(props.defaultData?.auto_price_update_details?.rounding_details);
	const [retailInfo, setRetailInfo] = useState<RetailPriceInfo | null>(null);
	const [isAmazonProduct, setIsAmazonProduct] = useState<boolean>(false);
	const [distributorLabelState, setDistributorLabelState] = useState<
		string | null
	>();

	const dispatch = useDispatch();
	const showAddSupplierDrawerDispatch = () =>
		dispatch(SettingsActionCreators.showAddOrEditSupplierDrawer());
	const hideAddSupplierDrawerDispatch = () =>
		dispatch(SettingsActionCreators.hideAddOrEditSupplierDrawer());

	useEffect(() => {
		if (!!props.defaultData?.selected_supplier_id) {
			props.getSupplierById?.(props.defaultData?.selected_supplier_id);
		}
	}, [props.defaultData?.selected_supplier_id]);

	useEffect(() => {
		if (isBundleEnhancementEnabled && props.defaultData) {
			setShowChildItems(
				!props.defaultData.hide_child_items_in_quote_block
			);
			setShowQuantity(!!props.defaultData.show_child_quantity);
			setShowPrice(!!props.defaultData?.show_individual_product_pricing);
		}
	}, [props.defaultData]);

	useEffect(() => {
		if (!!props.defaultData) {
			updatePricing(props.defaultData);
			setCustomFieldList(customFieldListProduct);
			if (defaultData?.selected_supplier_id) {
				props.getSupplierById?.(defaultData?.selected_supplier_id);
			} else {
				props.getSuppliers?.();
			}
			const isAmazonProduct =
				defaultData?.product_source === ProductSource.Amazon ||
				defaultData?.id?.includes(ProductSource.Amazon) ||
				(!!defaultData?.asin && defaultData?.asin !== "NA");
			setIsAmazonProduct(isAmazonProduct);
		}
	}, []);

	const showAddTaxCategoryDrawer = () => {
		dispatch(UsersActionCreators.addTaxCategoryDrawerOpened());
	};

	const closeAddTaxCategoryDrawer = () => {
		dispatch(UsersActionCreators.addTaxCategoryDrawerClosed());
	};

	const fetchProductDetails = async (productId: string) => {
		const productSource =
			props.defaultData?.product_source ===
			ProductSource.ZomentumMarketplace
				? "marketplace"
				: productId.startsWith("custom_distributor:")
				? ProductSource.CustomDistributor
				: productId.startsWith("dicker_data:")
				? ProductSource.DickerData
				: productId.startsWith(ProductSource.Etilize)
				? ProductSource.Etilize
				: productId.startsWith(ProductSource.Icecat)
				? ProductSource.Icecat
				: productId.startsWith(ProductSource.Amazon)
				? ProductSource.Amazon
				: "self";
		dispatch(ProductsActionCreators.showProductsLoader());
		const itemResponse = await getProductDetails(productId, productSource);
		dispatch(ProductsActionCreators.hideProductsLoader());
		return itemResponse;
	};

	const getIntialSelectedPricingId = (
		defaultData:
			| AddQuoteProductCatalogDrawerFormValues
			| EditQuoteProductCatalogDrawerFormValues
			| null
	) => {
		if (
			props.currentActiveDocument?.status === DocumentStatus.Won ||
			props.currentActiveDocument?.status === DocumentStatus.Lost
		) {
			return defaultData?.selected_pricing_id;
		}
		if (
			!!defaultData?.auto_price_update_details?.selected_distributors
				?.length
		) {
			return defaultData?.pricing?.find(
				pricing =>
					defaultData.auto_price_update_details
						?.selected_distributors &&
					pricing.distributor_pricing_details?.distributor &&
					!!defaultData.auto_price_update_details?.selected_distributors.find(
						distributor => {
							if (
								distributor.distributor ===
								Distributor.CustomDistributor
							) {
								return (
									distributor
										.selected_custom_distributor_details
										?.selected_custom_distributor_pricing_id ===
									pricing.distributor_pricing_details
										?.custom_distributor_pricing_details
										?.custom_distributor_pricing_id
								);
							}
							return (
								distributor.distributor ===
									pricing.distributor_pricing_details
										?.distributor &&
								pricing.vendor_sku_no ===
									defaultData.vendor_sku_number
							);
						}
					)
			)?.id;
		}
		if (defaultData?.selected_distributor) {
			const selectedPricing = defaultData.pricing?.find(pricing => {
				if (
					pricing.distributor_pricing_details?.distributor ===
					Distributor.CustomDistributor
				) {
					return (
						pricing.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor_pricing_id ===
						defaultData.selected_custom_distributor_details
							?.selected_custom_distributor_pricing_id
					);
				}

				return (
					pricing.distributor_pricing_details?.distributor ===
						defaultData.selected_distributor &&
					pricing.vendor_sku_no === defaultData.vendor_sku_number
				);
			});
			return selectedPricing?.id;
		} else if (isAmazonProduct) {
			return defaultData?.pricing?.[0]?.id ?? "";
		} else {
			return defaultData?.selected_pricing_id;
		}
	};

	const updatePricing = async (
		defaultData:
			| AddQuoteProductCatalogDrawerFormValues
			| EditQuoteProductCatalogDrawerFormValues
	) => {
		if (defaultData.vendor_product_id.includes("dell-quote-import")) {
			setIsAutoUpdateSwitchEnabled(false);
			return;
		}

		if (defaultData.product_source !== ProductSource.ZomentumMarketplace) {
			defaultData.pricing = await fetchPricingIfNeeded(
				defaultData.pricing,
				defaultData.vendor_product_id
			);
		}

		// update custom field only if required

		if (
			defaultData.product_source !== ProductSource.ZomentumMarketplace &&
			!defaultData.custom_fields
		)
			defaultData.custom_fields = await fetchCustomFieldsIfNeeded(
				defaultData.vendor_product_id
			);

		const defaultPricingId =
			defaultData.selected_distributor === Distributor.CustomDistributor
				? defaultData.pricing?.find(pricing => {
						return (
							pricing.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_distributor_pricing_id ===
							defaultData.selected_custom_distributor_details
								?.selected_custom_distributor_pricing_id
						);
				  })?.id
				: defaultData.selected_pricing_id ||
				  defaultData.pricing?.[0]?.id;
		const selectedPricingData = defaultData.pricing?.find(
			pricing =>
				pricing.id === defaultPricingId &&
				pricing.vendor_sku_no === defaultData.vendor_sku_number
		);

		const { markup, margin } = handleMarkupMargin(
			defaultData.selected_cost_price ||
				selectedPricingData?.cost_price ||
				0,
			defaultData.sell_price || selectedPricingData?.sell_price || 0,
			defaultData.quantity || 1,
			defaultData.should_add_setup_price_to_margin,
			defaultData.selected_setup_price ||
				selectedPricingData?.setup_price ||
				0
		);

		const isEtilizeProduct =
			props.defaultData?.product_source === ProductSource.Etilize;

		const productMargin =
			isEtilizeProduct || isAmazonProduct
				? defaultPreferences?.margin
				: margin;

		const productMarkup =
			isEtilizeProduct || isAmazonProduct
				? defaultPreferences?.markup
				: markup;

		const calculatedSellPrice = handleSellPriceCalculation(
			defaultPreferences?.markup ?? 0,
			defaultData.selected_cost_price
		);

		const productSellPrice =
			(defaultPreferences?.markup ?? 0) > 0 &&
			(defaultPreferences?.margin ?? 0) > 0 &&
			(props.isAddingProduct || isAmazonProduct) &&
			(isEtilizeProduct || isAmazonProduct)
				? calculatedSellPrice
				: !isAmazonProduct
				? defaultData.sell_price
				: defaultData.selected_cost_price;
		props.form.setFieldsValue({
			product_type: defaultData.product_type,
			selected_cost_price: defaultData.selected_cost_price,
			sell_price: productSellPrice,
			margin: productMargin,
			markup: productMarkup
		});
		const selectedPricingId = getIntialSelectedPricingId(defaultData);

		const defaultPricing = defaultData.pricing?.find(
			pricing => pricing.id === selectedPricingId
		);

		const retailInfo = defaultPricing?.distributor_pricing_details
			?.retail_price
			? ({
					distributor:
						defaultPricing.distributor_pricing_details?.distributor,
					price: defaultPricing.distributor_pricing_details
						?.retail_price,
					vendor_name:
						defaultPricing.distributor_pricing_details?.vendor_name
			  } as RetailPriceInfo)
			: null;

		const distributorLabelName = defaultPricing?.distributor_pricing_details
			?.distributor
			? defaultPricing?.distributor_pricing_details?.distributor ===
			  Distributor.CustomDistributor
				? defaultPricing?.distributor_pricing_details.vendor_name
				: distributorLabel[
						defaultPricing?.distributor_pricing_details?.distributor
				  ]
			: null;

		setDistributorLabelState(distributorLabelName);
		setRetailInfo(retailInfo);

		const defaultSelectedDistributor: DynamicPricingDistributors = {
			distributor: defaultData?.selected_distributor as Distributor,
			selected_warehouse: defaultData.selected_warehouse ?? null
		};

		if (
			!defaultData.pricing
				?.map(pricing => pricing.id)
				?.includes(defaultData.selected_pricing_id ?? "") &&
			!isAmazonProduct &&
			!!defaultData.selected_cost_price &&
			props.currentActiveDocument?.status !== DocumentStatus.Won &&
			props.currentActiveDocument?.status !== DocumentStatus.Lost
		) {
			const selectedDistributor = defaultData.pricing?.find(
				pricing =>
					pricing.vendor_name ===
						defaultSelectedDistributor.distributor &&
					pricing.vendor_sku_no === defaultData.vendor_sku_number &&
					!!pricing.distributor_pricing_details
			);

			const selectedDistributorCostPrice =
				selectedDistributor?.cost_price ??
				defaultData.selected_cost_price;

			const selectedDistributorSkuNo =
				selectedDistributor?.vendor_sku_no ??
				defaultData.vendor_sku_number;

			props.form.setFieldsValue({
				selected_cost_price: selectedDistributorCostPrice,
				vendor_sku_number: selectedDistributorSkuNo,
				sell_price: defaultData.sell_price
			});
		}

		if (
			defaultSelectedDistributor.distributor ===
			Distributor.CustomDistributor
		) {
			defaultSelectedDistributor.selected_custom_distributor_details =
				defaultData.selected_custom_distributor_details;
		}

		const selectedDistributorsValue = defaultData.auto_price_update_details
			?.is_enabled
			? defaultData.auto_price_update_details?.selected_distributors
			: [defaultSelectedDistributor];

		const isSelfProduct =
			!defaultData.manufacturer_part_no && !pricing?.[0]?.vendor_sku_no;

		const isAnyDistributorAvailable = defaultData.pricing?.some(
			pricing =>
				!!pricing.vendor_name?.length &&
				!!pricing.distributor_pricing_details
		);

		if (
			isSelfProduct ||
			props.isBundleProduct ||
			!isAnyDistributorAvailable
		) {
			setIsAutoUpdateSwitchEnabled(false);
			setIsAutoUpdatePricingEnabled(false);
		}

		setCatalogProductImages(defaultData.item_images || new Array<string>());
		setSelectedImages(defaultData.selected_images || new Array<string>());
		setPricing(defaultData.pricing);
		setSelectedDistributor(
			!!selectedPricingData?.distributor_pricing_details ||
				!!selectedPricingId?.length
				? defaultData.selected_distributor
				: undefined
		);
		setSelectedWarehouse(
			defaultData.selected_warehouse ??
				selectedPricingData?.distributor_pricing_details
					?.branch_wise_details[0]?.branch_name
		);
		setSelectedPricingId(selectedPricingId);
		setCanEditCostPrice(
			props.isBundleProduct
				? true
				: isNotABundleProduct
				? !selectedPricingId
				: !defaultPricing?.distributor_pricing_details
		);
		setIsAutoUpdatePricingEnabled(
			defaultData.auto_price_update_details?.is_enabled ?? false
		);
		setSelectedDistributors(selectedDistributorsValue);
	};

	const fetchPricingIfNeeded = async (
		pricing: Array<ProductPricingPlan>,
		vendorProductId?: string
	) => {
		try {
			const productId = vendorProductId ?? null;
			if (!productId) {
				throw new Error(
					"Unable to fetch details; There is no Product Id"
				);
			}

			const [itemResponse, quoteItemResponse] = await Promise.all([
				fetchProductDetails(productId),
				!props.isAddingProduct &&
					props.currentActiveDocument?.status !==
						DocumentStatus.Won &&
					props.currentActiveDocument?.status !==
						DocumentStatus.Lost &&
					getQuoteBlockProductPricing({
						product_id: props.defaultData?.vendor_product_id ?? "",
						pricing_id:
							props.defaultData?.selected_pricing_id ?? "",
						sku: props.defaultData?.vendor_sku_number ?? "",
						mfg_no: props.defaultData?.manufacturer_part_no ?? ""
					})
			]);

			// check for custom fields
			itemResponse.custom_fields &&
				setVendorCustomFields(itemResponse.custom_fields);

			let finalPricing = pricing;

			if (!props.isAddingProduct && quoteItemResponse) {
				finalPricing = quoteItemResponse.pricing;
			} else if (
				pricing?.length === 0 ||
				!productId.startsWith("etilize")
			) {
				finalPricing = itemResponse.pricing;
			}
			setPricing(finalPricing);
			return finalPricing;
		} catch (error) {
			console.error(error);
		}
		return pricing;
	};

	const fetchCustomFieldsIfNeeded = async (vendorProductId?: string) => {
		const productId = vendorProductId ?? null;
		if (!productId) {
			ZMessage.error("Unable to fetch details; There is no Product Id");
			return;
		}
		const itemResponse = await fetchProductDetails(productId);
		return itemResponse.custom_fields;
	};

	const onSubmit = ($event: React.FormEvent<HTMLFormElement>) => {
		$event.preventDefault();
		props.form.validateFieldsAndScroll(
			{
				scroll: {
					alignWithTop: true
				}
			},
			(
				errors: Record<string, string>,
				documentProductCatalogFormValues: QuoteProduct
			) => {
				if (!errors) {
					addDocumentProductCatalog($event, {
						...documentProductCatalogFormValues,
						selected_pricing_id: selectedPricingId
					});
				} else {
					console.warn(
						"DocumentProductConfigureDrawerOnSubmit",
						errors
					);
				}
			}
		);
	};

	const addDocumentProductCatalog = (
		_$event: any,
		documentProductConfigFormValues: QuoteProduct
	) => {
		const { custom_field_form_values, ...formValues } =
			documentProductConfigFormValues;
		const selectedPricingPlan = pricing?.find(
			pricing => pricing.id === selectedPricingId
		);

		if (isAutoUpdatePricingEnabled && !selectedDistributors?.length) {
			ZMessage.error(
				"Please select at least one distributor to use auto updated pricing"
			);
			return;
		}

		const quantityValue = formValues.quantity;
		const showIndividualPricing =
			formValues.show_individual_product_pricing;
		const defaultQuantity = props.defaultData?.quantity;
		const hasQuantityChanged = quantityValue !== defaultQuantity;

		const isQuantityChangedAndIndividualPricingDisabled =
			!showIndividualPricing && hasQuantityChanged;

		const isZeroQuantityNotSupported =
			quantityValue === 0 &&
			!allowQuantityZeroOrNegative?.zeroQuantity.value &&
			isQuantityChangedAndIndividualPricingDisabled;

		const isNegativeQuantityNotSupported =
			quantityValue < 0 &&
			!allowQuantityZeroOrNegative?.negativeQuantity.value &&
			isQuantityChangedAndIndividualPricingDisabled;

		if (isZeroQuantityNotSupported) {
			ZMessage.error(
				`Zero quantity is not supported by ${capitalizeFirstLetter(
					allowQuantityZeroOrNegative?.zeroQuantity?.name
				)}`
			);
			props.form.setFieldsValue({ quantity: defaultQuantity });
			return;
		} else if (isNegativeQuantityNotSupported) {
			ZMessage.error(
				`Negative quantity is not supported by ${capitalizeFirstLetter(
					allowQuantityZeroOrNegative?.negativeQuantity.name
				)}`
			);
			props.form.setFieldsValue({ quantity: defaultQuantity });
			return;
		}

		let updatedFormValues: QuoteProduct = {
			...props.defaultData,
			...formValues,
			selected_images: selectedImages,
			manufacturer:
				formValues.manufacturer === "NA" ||
				formValues.manufacturer === undefined
					? null
					: formValues.manufacturer,
			manufacturer_part_no:
				formValues.manufacturer_part_no === "NA" ||
				formValues.manufacturer_part_no === undefined
					? null
					: formValues.manufacturer_part_no,
			unit_of_measure: formValues.unit_of_measure,
			selected_distributor: selectedDistributor,
			selected_billing_period: formValues.selected_billing_period,
			selected_warehouse: selectedWarehouse,
			item_images: catalogProductImages || [],
			bundled_products: activeBundledQuoteProducts || [],
			mandatory_custom_field_check_require: true,
			vendor_name: selectedPricingPlan?.vendor_name,
			vendor_sku_number: formValues?.vendor_sku_number,
			hide_additional_price: !formValues?.selected_setup_price,
			auto_price_update_details: isAutoUpdatePricingEnabled
				? {
						is_enabled: isAutoUpdatePricingEnabled,
						selected_distributors: selectedDistributors ?? [],
						rounding_details:
							roundingDetails ?? new RoundingDetails()
				  }
				: null
		};

		// when toggling individual pricing, it gets ignored
		// by antd form
		updatedFormValues.sell_price = updatedFormValues.sell_price || 0;
		updatedFormValues.quantity = updatedFormValues.quantity || 1;
		if (custom_field_form_values && !!customFieldList?.length) {
			updatedFormValues = {
				...updatedFormValues,
				custom_fields: processCustomFieldFormValues(
					custom_field_form_values,
					customFieldList
				)
			};
		}

		if (
			updatedFormValues.selected_distributor ===
			Distributor.CustomDistributor
		) {
			updatedFormValues.selected_custom_distributor_details = {
				custom_distributor_id:
					selectedPricingPlan?.distributor_pricing_details
						?.custom_distributor_pricing_details?.custom_distributor
						.id || "",
				selected_custom_distributor_pricing_id:
					selectedPricingPlan?.distributor_pricing_details
						?.custom_distributor_pricing_details
						?.custom_distributor_pricing_id || "",
				custom_fields:
					selectedPricingPlan?.distributor_pricing_details
						?.custom_distributor_pricing_details?.custom_fields ??
					[]
			};
		}

		if (isBundleEnhancementEnabled && bundleSettingDetail) {
			updatedFormValues.hide_child_items_in_quote_block =
				!bundleSettingDetail.showChildItems;
			updatedFormValues.show_child_quantity =
				bundleSettingDetail.showQuantity;
			updatedFormValues.show_individual_product_pricing =
				bundleSettingDetail.showPrice;
		}

		props?.onSubmit?.(updatedFormValues);
	};

	const getFieldValues = () => {
		return props.form.getFieldsValue([
			"selected_cost_price",
			"sell_price",
			"quantity",
			"should_add_setup_price_to_margin",
			"selected_setup_price",
			"margin",
			"markup"
		]);
	};

	const onQuantityChange = (newQuantity?: number | string | null): void => {
		if (newQuantity === undefined || newQuantity === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				Number(newQuantity),
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	const onCostPriceChange = (newCostPrice?: ReactText | null): void => {
		if (newCostPrice === undefined) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				Number(newCostPrice),
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
		const selectedPricing = pricing?.find(
			pricing => pricing.id === selectedPricingId
		);
		if (selectedPricing?.distributor_pricing_details || isAmazonProduct) {
			setSelectedPricingId("");
		}
	};

	const onMarkupChange = (newMarkup?: ReactText | null): void => {
		if (
			newMarkup === undefined ||
			props.form.getFieldValue("selected_cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupChange(values, Number(newMarkup))
		);
	};

	const onMarginChange = (newMargin?: ReactText | null): void => {
		if (
			newMargin === undefined ||
			newMargin === null ||
			props.form.getFieldValue("selected_cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();

		if (
			Number(newMargin) >= 100 &&
			Number(getFieldValues().selected_cost_price) !== 0
		) {
			return;
		}
		props.form.setFieldsValue(
			handleMarginChange(values, Number(newMargin))
		);
	};

	const onSellPriceChange = (
		newSellPrice?: ReactText | number | null
	): void => {
		if (newSellPrice === undefined || newSellPrice === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				Number(newSellPrice),
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	const onSetupPriceChange = (newSetupPrice?: ReactText | null): void => {
		if (newSetupPrice === undefined || newSetupPrice === null) {
			return;
		}
		const values = getFieldValues();
		if (!values.should_add_setup_price_to_margin) {
			return;
		}
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				Number(newSetupPrice)
			)
		);
	};

	const onAddSetupPriceToMarginChange = (e: CheckboxChangeEvent): void => {
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				values.quantity,
				e.target.checked,
				values.selected_setup_price
			)
		);
	};

	const showCatalogProductImageUploadModal = () => {
		setCatalogProductImageUploadModalOpen(true);
	};

	const closeCatalogProductImageUploadModal = () => {
		setCatalogProductImageUploadModalOpen(false);
	};

	const deleteCatalogProductImage = (imageUrl: string) => {
		ZModal.confirm({
			title: "Are you sure you want to remove this image?",
			onOk: () => {
				setCatalogProductImages(prev =>
					prev.filter(img => img !== imageUrl)
				);
				setSelectedImages(prev => prev.filter(img => img !== imageUrl));
			}
		});
	};

	const uploadCatalogProductImage = (
		result: UploadResult<UppyFileMetaData>
	) => {
		if (!!result?.successful?.length) {
			const updatedCatalogImages = result.successful.map(
				image => image.uploadURL
			);

			setCatalogProductImages([
				...catalogProductImages,
				...updatedCatalogImages
			]);
			setSelectedImages([...selectedImages, ...updatedCatalogImages]);
		} else if (!!result?.failed?.length) {
			const failedError = result.failed[0].error;
			ZMessage.error(failedError);
		} else {
			ZMessage.error(DefaultErrorMessage);
		}
	};

	const handlePricingIdChange = (params: IHandlePricingIdChangeProps) => {
		try {
			const selectedPricingId = props.isNotABundleProduct
				? params?.value
				: params?.radioChangeEvent?.target.value;
			const distributor =
				pricing
					?.find(pricing => pricing.id === selectedPricingId)
					?.distributor_pricing_details?.distributor.toLowerCase() ??
				null;

			setSelectedDistributor(distributor ?? undefined);

			const selectedPricing = pricing?.find(
				p => p.id === selectedPricingId
			);

			setCanEditCostPrice(
				isNotABundleProduct
					? !!!selectedPricingId
					: !selectedPricing?.distributor_pricing_details
			);
			setSelectedPricingId(selectedPricingId);

			const distributorOrCatalog = pricing?.find(
				pricing => pricing.id === selectedPricingId
			);

			/* On pricing change: 
				1. If distributor/catalog is selected, get the supplier & SKU from pricing plan
				2. If custom is selected, retain the supplier id & SKU from the form. \
			*/

			const supplierId = !!distributorOrCatalog?.supplier_id?.length
				? distributorOrCatalog.supplier_id
				: props.form.getFieldValue("selected_supplier_id");

			const supplierSKU = !!distributorOrCatalog?.supplier_id?.length
				? distributorOrCatalog.vendor_sku_no
				: props.form.getFieldValue("vendor_sku_number");

			getSupplierById?.(supplierId);

			const margin: number = props.form.getFieldValue("margin") ?? 0;
			const markup: number = props.form.getFieldValue("markup") ?? 0;

			const calculatedSellPrice = !!selectedPricing
				? !!selectedPricing?.distributor_pricing_details ||
				  (isAmazonProduct &&
						!!selectedPricing?.amazon_business_pricing_details)
					? markup * 0.01 * (selectedPricing?.cost_price ?? 0) +
					  (selectedPricing?.cost_price ?? 0)
					: selectedPricing?.sell_price
				: markup *
						0.01 *
						(props.form.getFieldValue("cost_price") ?? 0) +
				  (props.form.getFieldValue("cost_price") ?? 0);

			props.form.setFieldsValue({
				selected_supplier_id: supplierId,
				vendor_sku_number: supplierSKU,
				selected_cost_price: selectedPricing?.cost_price || 0,
				selected_setup_price: selectedPricing?.setup_price || 0,
				selected_billing_period:
					selectedPricing?.billing_period ||
					ProductBillingType.OneTime,
				sell_price: calculatedSellPrice,
				margin: margin,
				markup: markup,
				asin: props.defaultData?.asin
			});

			const retailInfo = selectedPricing?.distributor_pricing_details
				?.retail_price
				? ({
						distributor:
							selectedPricing.distributor_pricing_details
								?.distributor,
						price: selectedPricing.distributor_pricing_details
							?.retail_price,
						vendor_name:
							selectedPricing.distributor_pricing_details
								.vendor_name
				  } as RetailPriceInfo)
				: null;
			const distributorLabelName = selectedPricing
				?.distributor_pricing_details?.distributor
				? selectedPricing?.distributor_pricing_details?.distributor ===
				  Distributor.CustomDistributor
					? selectedPricing?.distributor_pricing_details.vendor_name
					: distributorLabel[
							selectedPricing?.distributor_pricing_details
								?.distributor
					  ]
				: null;
			setDistributorLabelState(distributorLabelName);
			setRetailInfo(retailInfo);
		} catch (errors) {
			console.warn(
				"DocumentProductConfigureDrawerHandlePricingIdChange",
				errors
			);
		}
	};

	const handleWarehouseChange = (newWarehouse?: string, vendor?: string) => {
		setSelectedWarehouse(newWarehouse);
		if (isAutoUpdatePricingEnabled) {
			const updatedDistributors = selectedDistributors?.map(distributor =>
				distributor.distributor === (vendor as Distributor)
					? {
							distributor: vendor as Distributor,
							selected_warehouse: newWarehouse ?? null
					  }
					: distributor
			);
			setSelectedDistributors(updatedDistributors);
		}
	};

	const handleAutoUpdatePricingChange = (autoUpdatePricing: boolean) => {
		setIsAutoUpdatePricingEnabled(autoUpdatePricing);
	};

	const handleRoundingDetailsChange = (
		newRoundingDetails: RoundingDetails
	) => {
		setRoundingDetails(newRoundingDetails);
	};

	const handleSelectedDistributorsChange = (
		distributors:
			| {
					distributor: Distributor;
					selected_warehouse: string | null;
					vendor_name?: string;
			  }[]
			| null
	) => {
		const selectedDistributors = distributors?.map(distributor => {
			const distributorDetail: DynamicPricingDistributors = {
				distributor: distributor.distributor,
				selected_warehouse: distributor.selected_warehouse
			};
			if (distributor.distributor === Distributor.CustomDistributor) {
				const distributorId = customIntegrationList?.find(
					integration => {
						return (
							integration.integration_name ===
							distributor.vendor_name
						);
					}
				)?.id;
				const selectedPricing = pricing?.find(pricing => {
					return (
						pricing.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor.id === distributorId
					);
				});
				distributorDetail.selected_custom_distributor_details = {
					custom_distributor_id: distributorId || "",
					selected_custom_distributor_pricing_id:
						selectedPricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor_pricing_id || "",
					custom_fields:
						selectedPricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_fields ?? []
				};
			}
			return distributorDetail;
		});
		setSelectedDistributors(
			!!selectedDistributors?.length ? selectedDistributors : null
		);
	};

	const handlePricingsChange = (e: CheckboxChangeEvent) => {
		let selectedDistributorsValue = !!selectedDistributors?.length
			? [...selectedDistributors]
			: [];

		if (e.target.checked) {
			const selectedPricing = pricing?.find(
				pricing =>
					!!pricing.distributor_pricing_details &&
					pricing.id === e.target.value
			);
			const newDistributor: DynamicPricingDistributors = {
				distributor:
					selectedPricing?.distributor_pricing_details?.distributor ??
					Distributor.Ingram,
				selected_warehouse:
					selectedPricing?.distributor_pricing_details
						?.branch_wise_details?.[0]?.branch_name ?? null
			};
			if (
				selectedPricing?.distributor_pricing_details?.distributor ===
				Distributor.CustomDistributor
			) {
				const distributorId = customIntegrationList?.find(
					integration =>
						integration.integration_name ===
						selectedPricing?.distributor_pricing_details
							?.vendor_name
				)?.id;
				newDistributor.selected_custom_distributor_details = {
					custom_distributor_id: distributorId || "",
					selected_custom_distributor_pricing_id:
						selectedPricing.distributor_pricing_details
							.custom_distributor_pricing_details
							?.custom_distributor_pricing_id || "",
					custom_fields:
						selectedPricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_fields ?? []
				};
			}

			selectedDistributors?.push(newDistributor);
		} else {
			selectedDistributorsValue =
				selectedDistributors?.filter(
					(arr: DynamicPricingDistributors) => {
						const pricingUpdate = pricing?.find(
							pricing =>
								!!pricing.distributor_pricing_details &&
								pricing.id === e.target.value
						);

						if (arr.distributor === Distributor.CustomDistributor) {
							return (
								pricingUpdate?.distributor_pricing_details
									?.custom_distributor_pricing_details
									?.custom_distributor.id !==
								arr.selected_custom_distributor_details
									?.custom_distributor_id
							);
						}
						return (
							pricingUpdate?.distributor_pricing_details
								?.distributor !== arr.distributor
						);
					}
				) ?? [];
		}
		setSelectedDistributors(selectedDistributorsValue);

		const updatedPricingPlans = pricing?.filter(pricing =>
			selectedDistributorsValue?.find(distributorDetail => {
				if (
					distributorDetail.distributor ===
					Distributor.CustomDistributor
				) {
					return (
						pricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor.id ===
						distributorDetail.selected_custom_distributor_details
							?.custom_distributor_id
					);
				}
				return (
					pricing?.distributor_pricing_details?.distributor ===
					distributorDetail.distributor
				);
			})
		);

		const lowestPricedDistributorSortedArray = updatedPricingPlans?.sort(
			(prev, curr) =>
				prev.cost_price < curr.cost_price
					? -1
					: prev.cost_price > curr.cost_price
					? 1
					: 0
		);
		const lowestPricedDistributorCostPrice =
			lowestPricedDistributorSortedArray?.[0]?.cost_price ?? 0;

		const supplierId = !!lowestPricedDistributorSortedArray?.[0]
			?.supplier_id?.length
			? lowestPricedDistributorSortedArray?.[0]?.supplier_id
			: props.form.getFieldValue("selected_supplier_id");

		const supplierSKU = !!lowestPricedDistributorSortedArray?.[0]
			?.supplier_id?.length
			? lowestPricedDistributorSortedArray?.[0]?.vendor_sku_no
			: props.form.getFieldValue("vendor_sku_number");

		const calculatedSellPrice =
			props.form.getFieldValue("markup") > 0
				? (props.form.getFieldValue("markup") *
						lowestPricedDistributorCostPrice) /
						100 +
				  lowestPricedDistributorCostPrice
				: lowestPricedDistributorCostPrice;
		const calculatedSetupPrice = !!lowestPricedDistributorSortedArray?.[0]
			?.supplier_id?.length
			? lowestPricedDistributorSortedArray?.[0]?.setup_price
			: props.form.getFieldValue("selected_setup_price");

		props.form.setFieldsValue({
			selected_cost_price: lowestPricedDistributorCostPrice,
			sell_price: calculatedSellPrice,
			selected_supplier_id: supplierId,
			selected_setup_price: calculatedSetupPrice,
			vendor_sku_number: supplierSKU
		});
		setRetailInfo(null);
	};

	const onClose = (event: any) => {
		if (props.onClose) {
			props.onClose(event);
		}
	};

	const handleRowChange = (
		selectedVendorProductId: string,
		newProductData: Partial<BundledQuoteProduct>
	) => {
		let hideAdditionalPrice: boolean =
			activeBundledQuoteProducts.find(
				product => product.vendor_product_id === selectedVendorProductId
			)?.hide_additional_price ?? false;

		if (
			newProductData.selected_setup_price === 0 ||
			newProductData.selected_setup_price
		) {
			hideAdditionalPrice = newProductData.selected_setup_price === 0;
		}
		setActiveBundledQuoteProducts(
			activeBundledQuoteProducts.map(product =>
				product.vendor_product_id === selectedVendorProductId
					? {
							...product,
							...newProductData,
							hide_additional_price: hideAdditionalPrice
					  }
					: product
			)
		);
	};

	const handleRowDelete = (selectedVendorProductId: string) => {
		setActiveBundledQuoteProducts(
			activeBundledQuoteProducts.filter(
				product => product.vendor_product_id !== selectedVendorProductId
			)
		);
	};

	const checkAndAddProductToBundle = (quoteProducts: QuoteProduct[]) => {
		const data = quoteProducts[0];
		const quoteProduct: BundledQuoteProduct = {
			...data,
			is_optional: false,
			is_selected: false
		};

		if (data.product_type === ProductType.Bundle) {
			ZMessage.error("You cannot add a bundle inside another bundle");
			return;
		}

		const alreadyExist = activeBundledQuoteProducts.find(
			a => a.vendor_product_id === quoteProduct.vendor_product_id
		);

		if (!!alreadyExist) {
			ZModal.confirm({
				title: "Please Confirm",
				content:
					"Product already added to the bundle. Update the product with new values?",
				okText: "Yes",
				onOk: () => {
					handleAddProductToBundle(data);
				}
			});
		} else {
			handleAddProductToBundle(data);
		}
	};

	const handleAddProductToBundle = (data: QuoteProduct) => {
		try {
			const quoteProduct: BundledQuoteProduct = {
				...data,
				is_optional: false,
				is_selected: false
			};
			const productExistingInBundleIndex =
				activeBundledQuoteProducts.findIndex(
					product =>
						product.vendor_product_id ===
						quoteProduct.vendor_product_id
				);

			setBundleAddProductDrawerOpen(false);
			setActiveBundledQuoteProducts(
				productExistingInBundleIndex !== -1
					? [
							...activeBundledQuoteProducts.map(product =>
								product.vendor_product_id ===
								quoteProduct.vendor_product_id
									? quoteProduct
									: product
							)
					  ]
					: [...activeBundledQuoteProducts, quoteProduct]
			);

			const prevProductPricingValues = props.form.getFieldsValue([
				"selected_cost_price",
				"sell_price",
				"quantity"
			]);

			props.form.setFieldsValue({
				selected_cost_price:
					Math.abs(prevProductPricingValues.selected_cost_price) +
					data.selected_cost_price * data.quantity,
				sell_price:
					Math.abs(prevProductPricingValues.sell_price) +
					data.sell_price * data.quantity
			});

			ZMessage.success(`${data.name} is added to the bundle`);
		} catch (error) {
			if (error.name !== "ValidationError") {
				console.error(error);
			}
		}
	};

	const updateCostPriceFromBundleItems = () => {
		const costPrice =
			activeBundledQuoteProducts?.reduce(
				(total, curr) =>
					curr.is_optional
						? total
						: total + curr.selected_cost_price * curr.quantity,
				0
			) || 0;

		props.form.setFieldsValue({ selected_cost_price: costPrice });
	};

	const onSupplierAdd = async (supplierFormValues: SupplierFormValues) => {
		const supplier = await addSupplier?.(supplierFormValues);
		getSupplierById?.(supplier?.id);
		props.form.setFieldsValue({ selected_supplier_id: supplier?.id });
		return supplier;
	};

	const renderCostPriceInputGroup = () => {
		const {
			defaultData,
			form: { getFieldValue, getFieldDecorator }
		} = props;

		const calculatedSellPrice =
			(defaultPreferences?.markup ?? 0) *
				0.01 *
				(defaultData?.selected_cost_price ?? 0) +
			(defaultData?.selected_cost_price ?? 0);

		const productSellPrice =
			(defaultPreferences?.markup ?? 0) > 0 &&
			(defaultPreferences?.margin ?? 0) > 0 &&
			props.isAddingProduct
				? calculatedSellPrice
				: defaultData?.sell_price;

		const values = {
			selected_cost_price:
				getFieldValue("selected_cost_price") ??
				defaultData?.selected_cost_price ??
				0,
			sell_price:
				getFieldValue("sell_price") ??
				productSellPrice ??
				defaultData?.sell_price ??
				0,
			quantity: getFieldValue("quantity") ?? defaultData?.quantity ?? 0,
			should_add_setup_price_to_margin:
				getFieldValue("should_add_setup_price_to_margin") ??
				defaultData?.should_add_setup_price_to_margin ??
				0,
			selected_setup_price:
				getFieldValue("selected_setup_price") ??
				defaultData?.selected_setup_price ??
				0,
			product_type:
				getFieldValue("product_type") ?? defaultData?.product_type ?? 0
		};

		const { markup, margin } = handleMarkupMargin(
			values.selected_cost_price,
			values.sell_price,
			values.quantity,
			values.should_add_setup_price_to_margin,
			values.selected_setup_price
		);

		const forBundle = defaultData?.product_type === ProductType.Bundle;

		const isAmazonPricingSelected =
			!!userCompany.integrations.amazon_business?.connected_user
				?.length &&
			isAmazonProduct &&
			!!selectedPricingId;

		const isCatalogOrCustomPricingSelected =
			!!!selectedDistributor &&
			(!!selectedPricingId || !!!selectedPricingId?.length);

		const isCostPricingFieldsDisabled =
			props.isReadOnly ||
			(isBundleEnhancementEnabled &&
				bundleSettingDetail?.pricingDisabled);

		return (
			<Fragment>
				{!forBundle && (
					<>
						<Col span={24}>
							<Form.Item
								label="Supplier Name"
								labelAlign="left"
								hasFeedback={true}
							>
								{props.form.getFieldDecorator(
									"selected_supplier_id",
									{
										rules: [
											{
												required: false
											}
										],
										initialValue:
											defaultData?.selected_supplier_id
									}
								)(
									<ZSupplierSelect
										defaultOptions={suppliers}
										placeholder="Please enter supplier name"
										disabled={
											!isCatalogOrCustomPricingSelected ||
											props.isReadOnly ||
											isAmazonPricingSelected
										}
										dropdownRender={menu => (
											<div>
												{menu}
												<div
													className="add-entity-action"
													onMouseDown={$event => {
														$event.preventDefault();
													}}
													onClick={
														showAddSupplierDrawerDispatch
													}
												>
													Add Supplier
												</div>
											</div>
										)}
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Supplier SKU"
								labelAlign="left"
								hasFeedback={true}
							>
								{props.form.getFieldDecorator(
									"vendor_sku_number",
									{
										rules: [
											{
												required: false
											}
										],
										initialValue:
											defaultData?.vendor_sku_number
									}
								)(
									<Input
										disabled={
											!isCatalogOrCustomPricingSelected ||
											props.isReadOnly ||
											isAmazonPricingSelected
										}
										placeholder="Please enter supplier SKU"
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={24}>
							<Col span={24}>
								<Form.Item label="ASIN" labelAlign="left">
									{props.form.getFieldDecorator("asin", {
										rules: [
											{
												required: false,
												whitespace: true
											}
										],
										initialValue: defaultData?.asin || "NA"
									})(
										<ZInput
											placeholder="Please enter ASIN"
											disabled={
												!isCatalogOrCustomPricingSelected ||
												props.isReadOnly ||
												isAmazonPricingSelected
											}
											className="w-full"
										/>
									)}
								</Form.Item>
							</Col>
						</Col>
					</>
				)}
				<Col span={24}>
					<Form.Item
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
						label="Cost Price"
						labelAlign="left"
					>
						<ZTooltip
							title="Please disable auto update price to edit the cost price."
							showtooltip={isAutoUpdatePricingEnabled}
						>
							{getFieldDecorator("selected_cost_price", {
								rules: [
									{
										required: true,
										validator: (_, value) => {
											if (Number(value) < 0) {
												return Promise.reject(
													new Error(
														"Invalid Entry. Cost price cannot be less than 0"
													)
												);
											}
											if (!value && value !== 0) {
												return Promise.reject(
													new Error(
														"Cost price cannot be empty"
													)
												);
											}
											return Promise.resolve();
										}
									}
								],
								initialValue:
									defaultData?.selected_cost_price ?? 0
							})(
								<ZInputNumber
									data-testid="selected-cost-price"
									step=".01"
									placeholder="Please enter product cost price"
									min={0}
									onChange={onCostPriceChange}
									precision={2}
									disabled={
										isAutoUpdatePricingEnabled ||
										isCostPricingFieldsDisabled ||
										!isCatalogOrCustomPricingSelected ||
										isAmazonPricingSelected
									}
									addOnAfter={getCurrencySettings().currency}
									width={InputNumberWidths.full}
								/>
							)}
						</ZTooltip>
						{/* comment out for now untill we know expected behaviour */}
						{forBundle && (
							<ZFeatureAccess
								requiredRole={UserRole.SecondaryRole}
								requiredPlan={PricingPlanNames.IGNITE}
							>
								<ZButton
									className="sub-action-button"
									type="link"
									onClick={updateCostPriceFromBundleItems}
									disabled={isCostPricingFieldsDisabled}
								>
									<ZText className="text-blue-5 active:text-blue-6">
										<ZIcon
											name={EIcons.COPY}
											size="sm"
											className="mx-1 stroke-current"
										/>
										Copy Product Total
									</ZText>
								</ZButton>
							</ZFeatureAccess>
						)}
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Markup"
						labelAlign="left"
						hasFeedback={true}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						<Input.Group>
							{getFieldDecorator("markup", {
								rules: [
									{
										required: false,
										type: "number"
									}
								],
								initialValue: markup
							})(
								<InputNumber
									placeholder="Please enter product Markup"
									onChange={onMarkupChange}
									className="has-currency-addon"
									precision={2}
									step={0.1}
									disabled={isCostPricingFieldsDisabled}
								/>
							)}
							<Input
								placeholder={"%"}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Margin"
						labelAlign="left"
						hasFeedback={true}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						<Input.Group>
							{getFieldDecorator("margin", {
								rules: [
									{
										required: false,
										type: "number",
										validator(_, value) {
											if (
												Number(value) >= 100 &&
												getFieldValue(
													"selected_cost_price"
												) > 0
											) {
												return Promise.reject(
													new Error(
														"Invalid Entry. Margin cannot be 100% unless cost price is 0"
													)
												);
											}
											return Promise.resolve();
										}
									}
								],
								initialValue: margin
							})(
								<InputNumber
									placeholder="Please enter product Margin"
									max={100}
									onChange={onMarginChange}
									className="has-currency-addon"
									precision={2}
									step={0.1}
									disabled={isCostPricingFieldsDisabled}
								/>
							)}
							<Input
								placeholder={"%"}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
			</Fragment>
		);
	};

	const updateSellPriceFromBundleItems = () => {
		const sellPrice =
			activeBundledQuoteProducts?.reduce(
				(total, curr) =>
					curr.is_optional
						? total
						: total + curr.sell_price * curr.quantity,
				0
			) || 0;
		props.form.setFieldsValue({ sell_price: sellPrice });
	};

	const updateSellPriceByRetailPrice = () => {
		if (retailInfo?.price) {
			props.form.setFieldsValue({
				sell_price: retailInfo?.price
			});
			onSellPriceChange(retailInfo?.price);
		}
	};

	const updateSetupPriceFromBundleItems = () => {
		const totalSetupPrice =
			activeBundledQuoteProducts.reduce(
				(total, product) =>
					product.is_optional
						? total
						: total + product.selected_setup_price,
				0
			) || 0;
		props.form.setFieldsValue({
			selected_setup_price: totalSetupPrice
		});
	};

	const onSetupFeeLinkedToQuantityChange = (e: CheckboxChangeEvent) => {
		props.form.setFieldsValue({
			is_setup_fee_linked_to_quantity: e.target.checked
		});
	};

	const renderSellPriceInputGroup = () => {
		const {
			defaultData,
			form: { getFieldDecorator }
		} = props;
		const isEtilizeProduct =
			props.defaultData?.product_source === ProductSource.Etilize;

		const forBundle = defaultData?.product_type === ProductType.Bundle;

		const calculatedSellPriceForEtilizeProduct =
			(defaultPreferences?.markup ?? 0) *
				0.01 *
				(defaultData?.selected_cost_price ?? 0) +
			(defaultData?.selected_cost_price ?? 0);

		const calculatedSellPrice = pricing?.filter(
			pricing => pricing.id === selectedPricingId
		)[0]?.sell_price;

		const productSellPrice =
			(defaultPreferences?.markup ?? 0) > 0 &&
			(defaultPreferences?.margin ?? 0) > 0 &&
			(props.isAddingProduct || isAmazonProduct)
				? isEtilizeProduct || isAmazonProduct
					? calculatedSellPriceForEtilizeProduct
					: calculatedSellPrice ?? defaultData?.sell_price ?? 0
				: !isAmazonProduct
				? defaultData?.sell_price
				: defaultData?.selected_cost_price;

		const showRetailPriceCopy =
			!isAutoUpdatePricingEnabled &&
			retailInfo?.price &&
			props.isNotABundleProduct;

		const sellPrice = props.form.getFieldValue("sell_price");

		const showSampleSellPrice = !!(
			isAutoUpdatePricingEnabled &&
			roundingDetails?.is_enabled &&
			sellPrice
		);

		const roundingSample =
			showSampleSellPrice && roundingDetails
				? round(
						sellPrice,
						roundingDetails?.precision,
						roundingDetails?.method
				  )
				: "";

		const isSellPricingFieldsDisabled =
			props.isReadOnly ||
			(isBundleEnhancementEnabled &&
				bundleSettingDetail?.pricingDisabled);
		const selectedPricing = pricing?.find(
			pricing => pricing.id === selectedPricingId
		);
		const calculatedShippingCost =
			selectedPricing?.amazon_business_pricing_details
				?.shipping_cost?.[0] ??
			defaultData?.pricing?.[0]?.amazon_business_pricing_details
				?.shipping_cost?.[0] ??
			0;

		const onAddShippingFeesToSellPrice = (e: CheckboxChangeEvent): void => {
			const productSellPrice = props.form.getFieldValue("sell_price");
			if (calculatedShippingCost && e.target.checked) {
				const requiredKeys = [
					"selected_cost_price",
					"sell_price",
					"quantity",
					"should_add_setup_price_to_margin",
					"selected_setup_price",
					"product_type"
				];
				const values: Partial<QuoteProduct> =
					props.form.getFieldsValue(requiredKeys);
				const { markup, margin } = handleMarkupMargin(
					values.selected_cost_price || 0,
					values.sell_price + calculatedShippingCost || 0,
					values.quantity || 1,
					!!values.should_add_setup_price_to_margin,
					values.selected_setup_price || 0
				);
				props.form.setFieldsValue({
					margin,
					markup
				});
			} else if (calculatedShippingCost) {
				props.form.setFieldsValue({
					margin: defaultPreferences?.margin,
					markup: defaultPreferences?.markup
				});
			}

			if (e.target.checked && productSellPrice) {
				props.form.setFieldsValue({
					sell_price: productSellPrice + calculatedShippingCost
				});
			} else if (productSellPrice) {
				props.form.setFieldsValue({
					sell_price: productSellPrice - calculatedShippingCost
				});
			}
		};

		return (
			<Fragment>
				<Col span={24}>
					<Form.Item
						label="Billing Period"
						labelAlign="left"
						hasFeedback={true}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						{getFieldDecorator("selected_billing_period", {
							initialValue: defaultData?.selected_billing_period
						})(
							<Select<ProductBillingType>
								getPopupContainer={trigger =>
									trigger.parentNode
								}
								disabled={isSellPricingFieldsDisabled}
							>
								{(
									Object.keys(
										productBillingTypeOptions
									) as ProductBillingType[]
								).map(value => (
									<Select.Option key={value} value={value}>
										{productBillingTypeOptions[value]}
									</Select.Option>
								))}
							</Select>
						)}
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Quantity"
						labelAlign="left"
						hasFeedback={true}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						{getFieldDecorator("quantity", {
							rules: [
								{
									required: true,
									message: "Please enter product quantity"
								}
							],
							initialValue: defaultData?.quantity || 1
						})(
							<ZInputNumber
								placeholder="Please enter product quantity"
								min={Number.MIN_SAFE_INTEGER}
								width={InputNumberWidths.full}
								onChange={onQuantityChange}
								precision={0}
								disabled={isSellPricingFieldsDisabled}
							/>
						)}
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Sell Price"
						labelAlign="left"
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
						className={
							isAmazonProduct && !!selectedPricingId ? "mb-0" : ""
						}
					>
						<ZTooltip
							title="Sell price is auto calculated in real-time. Disable auto-update to edit Sell Price."
							showtooltip={
								isAutoUpdatePricingEnabled &&
								props.isNotABundleProduct
							}
						>
							{getFieldDecorator("sell_price", {
								rules: [
									{
										required: true,
										message:
											"This is not a valid price for this product"
									}
								],
								initialValue: productSellPrice ?? 0
							})(
								<ZInputNumber
									data-testid="product-sell-price"
									step=".01"
									placeholder="Please enter product sell price"
									min={0}
									onChange={onSellPriceChange}
									precision={2}
									disabled={
										isAutoUpdatePricingEnabled ||
										isSellPricingFieldsDisabled
									}
									addOnAfter={getCurrencySettings().currency}
									width={InputNumberWidths.full}
								/>
							)}
						</ZTooltip>
						{forBundle && (
							<ZButton
								className="sub-action-button"
								type="link"
								onClick={updateSellPriceFromBundleItems}
								disabled={isSellPricingFieldsDisabled}
							>
								<ZText className="text-blue-5 active:text-blue-6">
									<ZIcon
										name={EIcons.COPY}
										size="sm"
										className="mx-1 stroke-current"
									/>
									Copy Product Total
								</ZText>
							</ZButton>
						)}
					</Form.Item>
				</Col>
				{isAmazonProduct && !!selectedPricingId && (
					<Col span={20} offset={4}>
						<Form.Item
							className={
								showRetailPriceCopy || showSampleSellPrice
									? "mb-0"
									: ""
							}
						>
							{props.form.getFieldDecorator(
								"should_add_shipping_cost_to_sell_price",
								{
									valuePropName: "checked"
								}
							)(
								<ZCheckbox
									onChange={onAddShippingFeesToSellPrice}
									disabled={
										props.isReadOnly ||
										!isAmazonProduct ||
										!selectedPricingId ||
										!calculatedShippingCost
									}
								>
									{`Add Shipping Cost (${currencyFormatter(
										!!selectedPricingId
											? calculatedShippingCost
											: 0
									)}) to the Selling Price`}
								</ZCheckbox>
							)}
						</Form.Item>
					</Col>
				)}
				{showSampleSellPrice && (
					<Col span={24} offset={4}>
						<div className="mb-6 flex items-center">
							<ZText color={EColors.GRAY} colorVariant={7}>
								<span className="mr-2">Rounding</span>
								{sellPrice}
								{
									<ZIcon
										size="sm"
										className="mb-0.5 mx-2"
										name={EIcons.ARROWRIGHT}
										color={EColors.GRAY}
										colorVariant={6}
									/>
								}
								{roundingSample}
							</ZText>
						</div>
					</Col>
				)}
				{showRetailPriceCopy && retailInfo && !props.isReadOnly && (
					<Col span={24} offset={4}>
						<div className="mb-6 flex items-center">
							<ZText className="text-gray-7">
								{distributorLabelState}{" "}
								{ratailPriceLabel[retailInfo.distributor]}{" "}
								{currencyFormatter(retailInfo.price)}
							</ZText>
							<ZIcon
								onClick={updateSellPriceByRetailPrice}
								name={Icons.COPY}
								size="sm"
								className="mx-1 stroke-current text-blue-5 active:text-blue-6 cursor-pointer"
							/>
						</div>
					</Col>
				)}
				<Col span={24}>
					<Form.Item
						label="Setup Fee"
						labelAlign="left"
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						{getFieldDecorator("selected_setup_price", {
							rules: [
								{
									required: true,
									message: "Please enter product setup fee"
								}
							],
							initialValue: defaultData?.selected_setup_price || 0
						})(
							<ZInputNumber
								step=".01"
								placeholder="Please enter product setup fee"
								min={0}
								onChange={onSetupPriceChange}
								precision={2}
								disabled={isSellPricingFieldsDisabled}
								addOnAfter={getCurrencySettings().currency}
								width={InputNumberWidths.full}
							/>
						)}
						{forBundle && (
							<ZButton
								className="sub-action-button"
								type="link"
								onClick={updateSetupPriceFromBundleItems}
								disabled={isSellPricingFieldsDisabled}
							>
								<ZText className="text-blue-5 active:text-blue-6">
									<ZIcon
										name={EIcons.COPY}
										size="sm"
										className="mx-1 stroke-current"
									/>
									Copy Product Total
								</ZText>
							</ZButton>
						)}
					</Form.Item>
				</Col>
				<Col span={24} offset={4}>
					<Form.Item labelAlign="left" wrapperCol={{ span: 24 }}>
						{getFieldDecorator("is_setup_fee_linked_to_quantity", {
							valuePropName: "checked"
						})(
							<ZTooltip
								placement="bottomLeft"
								title="When enabled, the setup fee will be multiplied by the quantity of this item. If the quantity changes, the total setup fee will adjust accordingly."
							>
								<ZCheckbox
									checked={
										defaultData?.is_setup_fee_linked_to_quantity
									}
									onChange={onSetupFeeLinkedToQuantityChange}
									checkboxOption="Link Setup Fee to Quantity"
								/>
							</ZTooltip>
						)}
					</Form.Item>
				</Col>
			</Fragment>
		);
	};

	const handleAddBundledProductClose = () =>
		setBundleAddProductDrawerOpen(false);
	const handleAddBundledProductOpen = () =>
		setBundleAddProductDrawerOpen(true);
	const handleCreateProductOpen = () => setCreateProductButtonVisible(true);

	const handleCreateProductAdded = () => {
		setCreateProductButtonVisible(false);
		setBundleAddProductDrawerOpen(true);
	};

	const handleOnClose = () => setCreateProductButtonVisible(false);

	const handleEditBundledProductDrawerOpen = (
		editBundledQuoteProductDefaultState: BundledQuoteProduct
	) =>
		setEditBundledQuoteProductDefaultState(
			editBundledQuoteProductDefaultState
		);

	const handleEditBundledProductDrawerClose = () =>
		setEditBundledQuoteProductDefaultState(null);

	const handleEditBundledProductDrawerSubmit = ({
		id,
		...data
	}: QuoteProduct) => {
		const newData: BundledQuoteProduct = {
			...editBundledQuoteProductDefaultState,
			...data,
			is_optional: !!editBundledQuoteProductDefaultState?.is_optional,
			is_selected: !!editBundledQuoteProductDefaultState?.is_selected
		};

		const newActiveBundledQuoteProducts = activeBundledQuoteProducts.map(
			a =>
				a.vendor_product_id === newData.vendor_product_id ? newData : a
		);

		setEditBundledQuoteProductDefaultState(null);
		setActiveBundledQuoteProducts(newActiveBundledQuoteProducts);
	};

	const handleProductVisible = () => {
		const values: Partial<QuoteProduct> = props.form.getFieldsValue([
			"hide_child_items_in_quote_block"
		]);
		props.form.setFieldsValue({
			hide_child_items_in_quote_block:
				!values.hide_child_items_in_quote_block
		});
	};

	const getInitialTaxCategoryId = (
		defaultData:
			| AddQuoteProductCatalogDrawerFormValues
			| EditQuoteProductCatalogDrawerFormValues
			| null
	): string | undefined => {
		if (
			defaultData?.tax_category_id &&
			defaultData?.tax_category_id?.length > 0
		) {
			const defaultTaxCategory = taxCategoriesList.find(
				taxCategory => taxCategory.id === defaultData.tax_category_id
			);
			if (!defaultTaxCategory || defaultTaxCategory?.disabled) {
				return;
			}
			return defaultData?.tax_category_id;
		} else if (defaultData?.product_source === ProductSource.Etilize) {
			return taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.includes(
						ProductType.Hardware
					)
			)?.id;
		} else if (
			defaultData?.product_type &&
			defaultData?.product_type?.length > 0
		) {
			return taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.includes(
						defaultData?.product_type as ProductType
					)
			)?.id;
		}

		return undefined;
	};

	const getInitialUnitOfMeasure = (
		defaultData:
			| AddQuoteProductCatalogDrawerFormValues
			| EditQuoteProductCatalogDrawerFormValues
			| null
	) => {
		if (!!defaultData?.unit_of_measure?.length) {
			return defaultData.unit_of_measure;
		} else if (!!defaultData?.product_type?.length) {
			return unitOfMeasureMapping?.find(
				mapping => mapping.item_type === defaultData.product_type
			)?.unit_of_measure;
		}
		return undefined;
	};

	const onProductTypeChange = (productType?: string) => {
		if (productType) {
			const tax_category_id = taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.findIndex(
						defaultProductType =>
							defaultProductType === (productType as ProductType)
					) > -1
			)?.id;
			const unit_of_measure = unitOfMeasureMapping?.find(
				mapping => mapping.item_type === productType
			)?.unit_of_measure;
			props.form.setFieldsValue({
				tax_category_id,
				unit_of_measure
			});
		}
	};

	const updateBundleProductOrder = (
		bundleProducts: BundledQuoteProduct[]
	) => {
		setActiveBundledQuoteProducts(bundleProducts);
	};

	const getProductFormItem = (customField: CustomField): ReactNode => {
		const { defaultData } = props;
		const requiredKeys = [
			"selected_cost_price",
			"sell_price",
			"quantity",
			"should_add_setup_price_to_margin",
			"selected_setup_price",
			"show_individual_product_pricing",
			"hide_child_items_in_quote_block",
			"product_type"
		];
		const values: Partial<QuoteProduct> =
			props.form.getFieldsValue(requiredKeys);
		const { margin } = handleMarkupMargin(
			values.selected_cost_price || 0,
			values.sell_price || 0,
			values.quantity || 1,
			!!values.should_add_setup_price_to_margin,
			values.selected_setup_price || 0
		);
		const marginAmount =
			((values.sell_price || 0) - (values.selected_cost_price || 0)) *
				(values.quantity || 1) +
			(!!values.should_add_setup_price_to_margin
				? values.selected_setup_price || 0
				: 0);

		const isBundleProduct = values.product_type === ProductType.Bundle;

		const isAmazonPricingSelected = isAmazonProduct && !!selectedPricingId;

		switch (customField.system_field_id) {
			case "item_type": {
				return (
					<Fragment>
						<Col span={24}>
							<Form.Item
								label="Type"
								labelAlign="left"
								hasFeedback={true}
							>
								{props.form.getFieldDecorator("product_type", {
									rules: [
										{
											required: true,
											message:
												"Please select product type"
										}
									],
									initialValue: values.product_type ?? ""
								})(
									<Select<ProductType>
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										disabled={
											props.isReadOnly ||
											values.product_type ===
												ProductType.Bundle
										}
										allowClear={true}
										placeholder="Please select product type"
										onChange={onProductTypeChange}
									>
										<Select.Option
											value={ProductType.Hardware}
										>
											Hardware
										</Select.Option>
										<Select.Option
											value={ProductType.Software}
										>
											Software
										</Select.Option>
										<Select.Option
											value={ProductType.Service}
										>
											Service
										</Select.Option>
										<Select.Option
											value={ProductType.Labor}
										>
											Labor
										</Select.Option>
										<Select.Option
											value={ProductType.Bundle}
											disabled
										>
											Bundle
										</Select.Option>
									</Select>
								)}
							</Form.Item>
						</Col>
					</Fragment>
				);
			}
			case "item_item_images": {
				const fileUploadModalProps: IModalProps = {
					visible: catalogProductImageUploadModalOpen,
					title: "Upload image"
				};
				return (
					<Fragment>
						<Col span={24}>
							<Form.Item label="Product Images" labelAlign="left">
								<div className="product-image-checkbox-container">
									{catalogProductImages.map(
										(catalogProductImage, index) => (
											<ZCheckbox
												key={index}
												className="product-image-checkbox"
												disabled={props.isReadOnly}
												value={catalogProductImage}
												checked={selectedImages.includes(
													catalogProductImage
												)}
												onChange={e => {
													const { checked } =
														e.target;
													setSelectedImages(prev =>
														checked
															? [
																	...prev,
																	catalogProductImage
															  ]
															: prev.filter(
																	img =>
																		img !==
																		catalogProductImage
															  )
													);
												}}
											>
												<ZImage
													className="product-image"
													src={getProductImageUrl(
														catalogProductImage
													)}
													data-testid="product-thumbnail-image"
													alt="Product Thumbnail"
													fallback={fallbackImageURL}
												/>
												{!props.isReadOnly && (
													<span
														className="product-image-delete-btn"
														role="button"
														tabIndex={0}
														aria-label="Delete product image"
														onMouseDown={e => {
															e.preventDefault();
															e.stopPropagation();
														}}
														onClick={e => {
															e.preventDefault();
															e.stopPropagation();
															deleteCatalogProductImage(
																catalogProductImage
															);
														}}
														onKeyDown={e => {
															if (
																e.key ===
																	"Enter" ||
																e.key === " "
															) {
																e.preventDefault();
																e.stopPropagation();
																deleteCatalogProductImage(
																	catalogProductImage
																);
															}
														}}
													>
														<Icon type="delete" />
													</span>
												)}
											</ZCheckbox>
										)
									)}
									{!props.isReadOnly && (
										<div
											className="product-image-file-upload"
											onClick={
												showCatalogProductImageUploadModal
											}
										>
											<Icon type="upload" />
											<span>Upload</span>
										</div>
									)}
								</div>
								{process.env.NODE_ENV !== "test" && (
									<ZFileUpload
										maxNumberOfFiles={5}
										minNumberOfFiles={1}
										useModal={fileUploadModalProps}
										useURL
										allowedFileTypes={[
											AllowedFileTypes.IMAGE
										]}
										maxFileSize={26214400}
										allowMultipleUploads={true}
										onCancel={
											closeCatalogProductImageUploadModal
										}
										onUploadSuccess={result => {
											closeCatalogProductImageUploadModal();
											uploadCatalogProductImage(result);
										}}
									/>
								)}
							</Form.Item>
						</Col>
					</Fragment>
				);
			}
			case "item_description": {
				return (
					<Col span={24}>
						<Form.Item
							label="Description"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("description", {
								initialValue: he.decode(
									defaultData?.description ?? ""
								)
							})(
								<TextArea
									placeholder="Please enter product description"
									autoSize={{ minRows: 3 }}
									allowClear={true}
									className="zomentum-sm-textarea"
									disabled={props.isReadOnly}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "item_pricing": {
				return <Fragment />;
			}
			case "item_product_category": {
				return <Fragment />;
			}
			case "item_manufacturer": {
				return (
					!isBundleProduct && (
						<Col span={24}>
							<Form.Item label="Manufacturer" labelAlign="left">
								{props.form.getFieldDecorator("manufacturer", {
									rules: [
										{
											required: false,
											whitespace: true
										}
									],
									initialValue:
										defaultData?.manufacturer || "NA"
								})(
									<Input
										placeholder="Please enter manufacturer name."
										disabled={
											props.isReadOnly ||
											isAmazonPricingSelected
										}
									/>
								)}
							</Form.Item>
						</Col>
					)
				);
			}
			case "item_manufacturer_part_no": {
				return (
					!isBundleProduct && (
						<Col span={24}>
							<Form.Item label="Mfg. Number" labelAlign="left">
								{props.form.getFieldDecorator(
									"manufacturer_part_no",
									{
										rules: [
											{
												required: false,
												whitespace: true
											}
										],
										initialValue:
											defaultData?.manufacturer_part_no ||
											"NA"
									}
								)(
									<Input
										placeholder="Please enter manufacturer part number"
										disabled={
											props.isReadOnly ||
											isAmazonPricingSelected
										}
									/>
								)}
							</Form.Item>
						</Col>
					)
				);
			}
			case "item_vendor_name": {
				return <Fragment />;
			}
			case "item_vendor_sku_no": {
				return <Fragment />;
			}
			case "item_tax_category_id": {
				const initialTaxCategoryId =
					getInitialTaxCategoryId(defaultData);
				return (
					<Col span={24}>
						<Form.Item
							label="Tax Category"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("tax_category_id", {
								rules: [
									{
										required:
											!!props.currentActiveDocument
												?.tax_region_id,
										message: "Please enter tax category"
									}
								],
								initialValue: initialTaxCategoryId
							})(
								<TaxSelect
									data-testid="document-product-tax-category"
									getPopupContainer={trigger =>
										trigger.parentNode
									}
									placeholder="Please select tax category"
									loading={isLoading}
									disabled={isLoading || props.isReadOnly}
									dropdownRender={menu => (
										<div>
											{menu}
											<RoleBasedDisplayWrapper
												requiredRole={
													UserRole.PrimaryRole
												}
											>
												{hasTaxEditPermission && (
													<div
														className="add-entity-action"
														onMouseDown={$event => {
															$event.preventDefault();
														}}
														onClick={
															showAddTaxCategoryDrawer
														}
													>
														Add Tax Category
													</div>
												)}
											</RoleBasedDisplayWrapper>
										</div>
									)}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "item_should_add_setup_price_to_margin": {
				return (
					<Fragment>
						{isBundleProduct && (
							<Fragment>
								{!values.show_individual_product_pricing &&
									!isBundleEnhancementEnabled && (
										<Col span={24} className="mb-3">
											<Row justify="end">
												<ZButton
													type="link"
													onClick={
														handleProductVisible
													}
													disabled={props.isReadOnly}
												>
													{!!values.hide_child_items_in_quote_block
														? `Show`
														: `Hide`}{" "}
													All Products
												</ZButton>
											</Row>
										</Col>
									)}
								<Col span={24} className="mb-6">
									<BundleProductDrawerTable
										data={activeBundledQuoteProducts}
										onRowChange={handleRowChange}
										onRowDelete={handleRowDelete}
										onAddProductButtonClick={
											handleAddBundledProductOpen
										}
										onCreateProductButtonClick={
											handleCreateProductOpen
										}
										onOpenEditDrawer={
											handleEditBundledProductDrawerOpen
										}
										isReadOnly={props.isReadOnly}
										updateBundleProductOrder={
											updateBundleProductOrder
										}
									/>
								</Col>
								{isBundleEnhancementEnabled ? (
									<Col>
										<div className="mb-6 mt-6">
											<ZText className="mr-4">
												Show child items
											</ZText>
											<Switch
												onChange={
													bundleSettingDetail?.handleChangeChildItems
												}
												size="small"
												checked={
													bundleSettingDetail?.showChildItems
												}
											/>
											{bundleSettingDetail?.showChildItems && (
												<>
													<div className="flex items-center mt-1">
														<ZIcon
															name={
																EIcons.INFOCIRCLE
															}
															size="sm"
															className="m-0"
														/>
														<ZText
															colorVariant={8}
															level={0}
															className="ml-2"
														>
															Please note that you
															will not be able to
															edit the product
															bundle pricing
															fields if the{" "}
															<ZText
																colorVariant={9}
																level={0}
															>
																Show Price
															</ZText>{" "}
															is toggled on.
														</ZText>
													</div>
													<div className="ml-9 mt-4">
														<ZText className="mr-4">
															Show quantity
														</ZText>
														<Switch
															onChange={
																bundleSettingDetail?.handleChangeQuantity
															}
															size="small"
															checked={
																bundleSettingDetail?.showQuantity
															}
														/>
														<div className="mb-2"></div>
														<ZText className="mr-9">
															Show price
														</ZText>
														<Switch
															onChange={
																bundleSettingDetail?.handleChangePrice
															}
															size="small"
															checked={
																bundleSettingDetail?.showPrice
															}
														/>
													</div>
												</>
											)}
										</div>
									</Col>
								) : (
									<Col span={24} className="mb-3">
										<Row justify="end">
											{props.form.getFieldDecorator(
												"show_child_quantity",
												{
													initialValue:
														!!defaultData?.show_child_quantity,
													valuePropName: "checked"
												}
											)(
												<ZCheckbox
													disabled={props.isReadOnly}
												>
													Display individual quantity
													on quote block
												</ZCheckbox>
											)}
										</Row>
									</Col>
								)}
								<Col span={24} className="mb-6">
									<Row className="pricing-data-box">
										{!isBundleEnhancementEnabled && (
											<Col span={24} className="mb-6">
												{props.form.getFieldDecorator(
													"show_individual_product_pricing",
													{
														initialValue:
															!!defaultData?.show_individual_product_pricing,
														valuePropName: "checked"
													}
												)(
													<ZCheckbox
														disabled={
															props.isReadOnly
														}
													>
														Show individual product
														pricing
													</ZCheckbox>
												)}
											</Col>
										)}
										<Row
											className={`${
												!isBundleEnhancementEnabled
													? props.form.getFieldValue(
															"show_individual_product_pricing"
													  )
														? "hidden"
														: ""
													: ""
											}`}
										>
											{renderCostPriceInputGroup()}
											<Divider className="mb-6" />
											{renderSellPriceInputGroup()}
											<Col span={24}>
												{props.form.getFieldDecorator(
													"should_add_setup_price_to_margin",
													{
														valuePropName:
															"checked",
														initialValue:
															!!defaultData?.should_add_setup_price_to_margin
													}
												)(
													<ZCheckbox
														onChange={
															onAddSetupPriceToMarginChange
														}
														disabled={
															props.isReadOnly ||
															(isBundleEnhancementEnabled &&
																bundleSettingDetail?.pricingDisabled)
														}
													>
														Add 'Setup Fee' to
														Margin
													</ZCheckbox>
												)}
												<div className="margin-preview">
													<strong>
														Item Margin:
													</strong>{" "}
													<span>
														{percentageFormatter(
															margin,
															100
														)}{" "}
														(
														{currencyFormatter(
															marginAmount
														)}
														)
													</span>
												</div>
											</Col>
										</Row>
									</Row>
								</Col>
							</Fragment>
						)}
						{!isBundleProduct && (
							<Fragment>
								<Col span={24}>
									{props.isNotABundleProduct ? (
										<DynamicPricingOptions
											handlePricingsChange={
												handlePricingsChange
											}
											pricing={pricing}
											selectedPricingId={
												selectedPricingId
											}
											selectedWarehouse={
												selectedWarehouse
											}
											handlePricingIdChange={
												handlePricingIdChange
											}
											handleWarehouseChange={
												handleWarehouseChange
											}
											handleAutoUpdatePricingChange={
												handleAutoUpdatePricingChange
											}
											handleSelectedDistributorsChange={
												handleSelectedDistributorsChange
											}
											isAutoUpdateSwitchEnabled={
												isAutoUpdateSwitchEnabled
											}
											isAutoUpdatePricingEnabled={
												isAutoUpdatePricingEnabled
											}
											selectedDistributors={
												selectedDistributors
											}
											roundingDetails={roundingDetails}
											handleRoundingDetailsChange={
												handleRoundingDetailsChange
											}
											calculatedSellPrice={props.form.getFieldValue(
												"sell_price"
											)}
											isReadOnly={props.isReadOnly}
											isAmazonProduct={isAmazonProduct}
											costPrice={
												defaultData?.selected_cost_price
											}
											currentActiveDocument={
												props.currentActiveDocument
											}
										/>
									) : (
										<ProductPricingOptions
											pricing={pricing}
											selectedPricingId={
												selectedPricingId
											}
											selectedWarehouse={
												selectedWarehouse
											}
											handlePricingIdChange={
												handlePricingIdChange
											}
											handleWarehouseChange={
												handleWarehouseChange
											}
											isReadOnly={props.isReadOnly}
										/>
									)}
								</Col>
								{renderCostPriceInputGroup()}
								{renderSellPriceInputGroup()}
								<Col span={24}>
									{props.form.getFieldDecorator(
										"should_add_setup_price_to_margin",
										{
											valuePropName: "checked",
											initialValue:
												!!defaultData?.should_add_setup_price_to_margin
										}
									)(
										<ZCheckbox
											onChange={
												onAddSetupPriceToMarginChange
											}
											disabled={props.isReadOnly}
										>
											Add 'Setup Fee' to Margin
										</ZCheckbox>
									)}
									<div className="margin-preview my-6">
										<strong>Item Margin:</strong>{" "}
										<span>
											{percentageFormatter(margin, 100)} (
											{currencyFormatter(marginAmount)})
										</span>
									</div>
								</Col>
							</Fragment>
						)}
					</Fragment>
				);
			}
			case "item_show_individual_product_pricing":
			case "item_tax_type":
				return <Fragment />;
			case "item_unit_of_measure": {
				const uomOptions = customField.allowed_dropdown_values?.map(
					value => ({
						value,
						name: value
					})
				);
				const initialUnitOfMeasure =
					getInitialUnitOfMeasure(defaultData);
				return (
					<>
						<Col span={24}>
							<Form.Item
								label="UOM"
								labelAlign="left"
								hasFeedback={true}
								className="mb-2"
							>
								{props.form.getFieldDecorator(
									"unit_of_measure",
									{
										initialValue: initialUnitOfMeasure,
										rules: [
											{
												required:
													customField
														?.mandatory_field_details?.[
														CustomFieldMandatoryFieldDetailsValues
															.FOR_UI
													],
												message:
													"Please select the unit of measure"
											}
										]
									}
								)(
									<ZSelect
										placeholder="Please select the unit of measure"
										allowClear
										selectOptions={uomOptions}
										disabled={props.isReadOnly}
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={20} offset={4} className="mb-6">
							<ProductUOMWarning
								currentUOM={props.form.getFieldValue(
									"unit_of_measure"
								)}
								parentProductId={
									defaultData?.vendor_product_id ?? null
								}
								productSource={defaultData?.product_source}
							/>
						</Col>
					</>
				);
			}

			case "quickbooks_item_type": {
				const quickbooksCustomField = vendorCustomFields?.find(
					customField =>
						customField.system_field_id === "quickbooks_item_type"
				);

				const itemQuickbooksItemTypeValues =
					props.defaultData?.custom_fields?.find(
						customField =>
							customField.system_field_id ===
							"quickbooks_item_type"
					)?.values;

				const values =
					itemQuickbooksItemTypeValues ??
					quickbooksCustomField?.values;

				const isReadonly = !!values?.length || props.isReadOnly;

				return (
					<Col span={24}>
						<RenderCustomFieldFormItem
							form={props.form}
							customField={customField}
							values={values}
							shouldShowTooltip
							customFieldList={customFieldList}
							isReadonly={isReadonly}
							formAlignment={{
								labelCol: { span: 4 },
								wrapperCol: { span: 20 }
							}}
						/>
					</Col>
				);
			}

			case "item_asin": {
				return <Fragment />;
			}

			default: {
				const productCustomField = defaultData?.custom_fields?.find(
					field => field.display_name === customField.display_name
				);
				let values = productCustomField?.values;

				const selectedPricing = pricing?.find(
					pricing => pricing.id === selectedPricingId
				);
				const customDistributorCustomFields =
					selectedPricing?.distributor_pricing_details
						?.custom_distributor_pricing_details?.custom_fields;

				if (!!customDistributorCustomFields?.length) {
					values = customDistributorCustomFields.find(
						field => field.id === productCustomField?.id
					)?.values;
				}
				const isEtilizeProduct = isEtilizeVendorProductId(
					props.defaultData?.vendor_product_id ?? ""
				);
				const isRealtimeSync =
					userCompany?.integrations.connectwise?.sync_option ===
					SyncOption.RealTimeSync;

				const isReadonly =
					props.isReadOnly ||
					(isEtilizeProduct &&
						isRealtimeSync &&
						!props.isAddingProduct);
				return (
					<Col span={24}>
						<RenderCustomFieldFormItem
							form={props.form}
							customField={customField}
							values={values}
							customFieldList={customFieldList}
							isReadonly={isReadonly}
							formAlignment={{
								labelCol: { span: 4 },
								wrapperCol: { span: 20 }
							}}
						/>
					</Col>
				);
			}
		}
	};

	const {
		defaultData,
		form: { getFieldDecorator }
	} = props;

	getFieldDecorator("product_type", {
		initialValue: defaultData?.product_type
	});
	getFieldDecorator("hide_child_items_in_quote_block", {
		initialValue: defaultData?.hide_child_items_in_quote_block
	});
	const isEditBundledProductDrawerVisible =
		!!editBundledQuoteProductDefaultState;

	const handleTaxCategoryDrawerOnSubmit = (id: string) => {
		props.form.setFieldsValue({
			tax_category_id: id
		});
	};

	return (
		<Fragment>
			<AddTaxCategoryDrawer
				visible={addTaxCategoryDrawerOpen}
				onClose={closeAddTaxCategoryDrawer}
				handleTaxCategoryDrawerOnSubmit={
					handleTaxCategoryDrawerOnSubmit
				}
			/>
			<Form
				layout="horizontal"
				labelCol={{ span: 4 }}
				wrapperCol={{ span: 20 }}
				onSubmit={onSubmit}
				className="add-document-product-catalog-drawer-form"
				noValidate
			>
				<ZSpin spinning={isLoading}>
					<Row className="drawer-form-container pb-4">
						<Col span={24}>
							<Form.Item
								label="Title"
								labelAlign="left"
								hasFeedback={true}
							>
								{getFieldDecorator("name", {
									rules: [
										{
											required: true,
											message:
												"Please enter product name",
											whitespace: true
										}
									],
									initialValue: defaultData?.name ?? ""
								})(
									<Input
										placeholder="Please enter product name"
										disabled={props.isReadOnly}
									/>
								)}
							</Form.Item>
						</Col>
						{customFieldList?.map(customField => {
							return (
								<Fragment key={customField.id}>
									{getProductFormItem(customField)}
								</Fragment>
							);
						})}
					</Row>
				</ZSpin>

				{!props.isReadOnly && (
					<div className="ant-drawer-footer">
						<ZButton
							type="default"
							htmlType="button"
							onClick={onClose}
							className="mr-2"
						>
							Cancel
						</ZButton>
						<ZButton
							type="primary"
							data-testid="submit-button"
							htmlType="submit"
							disabled={isLoading}
						>
							{props.okText}
						</ZButton>
					</div>
				)}
			</Form>
			<AddProductToQuoteBlockDrawer
				visible={bundleAddProductDrawerOpen}
				onClose={handleAddBundledProductClose}
				onSubmit={checkAndAddProductToBundle}
				itemType={props.defaultData?.product_type ?? undefined}
				document={props.currentActiveDocument}
			/>
			<AddProductDrawerV2
				shouldRedirectToProduct={false}
				visible={createProductButtonVisible}
				onClose={handleOnClose}
				onAdd={handleCreateProductAdded}
				drawerState={EProductDrawerState.ADD}
			/>
			<ZAddSupplier
				visible={addOrEditSupplierDrawerOpen}
				closeDrawer={hideAddSupplierDrawerDispatch}
				addSupplier={onSupplierAdd}
				isFormLoading={props.isSupplierSubmitting}
			/>
			<ZDrawer
				visible={isEditBundledProductDrawerVisible}
				closable={true}
				destroyOnClose={true}
				onClose={handleEditBundledProductDrawerClose}
				className="document-product-drawer edit-document-product-drawer zomentum-drawer"
				title={
					<Row>
						<Col span={24}>
							<div className="drawer-title-container">
								<ZText className="drawer-title">
									Configure Product
								</ZText>
							</div>
						</Col>
					</Row>
				}
			>
				<ConnectedDocumentProductConfigureDrawerForm
					okText="Update Product"
					defaultData={
						(!!editBundledQuoteProductDefaultState &&
							EditQuoteProductCatalogDrawerFormValues.fromBundledQuoteProduct(
								editBundledQuoteProductDefaultState
							)) ||
						null
					}
					onClose={handleEditBundledProductDrawerClose}
					onSubmit={handleEditBundledProductDrawerSubmit}
					currentActiveDocument={props.currentActiveDocument}
					isBundleProduct={true}
					isReadOnly={props.isReadOnly}
				/>
			</ZDrawer>
		</Fragment>
	);
};

const ConnectedDocumentProductConfigureDrawerForm = Form.create<Props>({
	name: "configure_document_product_catalog_form"
})((props: Props) => {
	return (
		<DocumentProductConfigureDrawerForm
			{...props}
			isReadOnly={props.isReadOnly ?? false}
		/>
	);
});

export default ConnectedDocumentProductConfigureDrawerForm;
